/**
 * ISSUE-1385. This team's season stats with league ranks (sports-api POST
 * /team/season-stats-get), collapsed into the same three states as
 * `useTeamStats`, for the same reason: a league with no team stats feed and a
 * failed read both mean the Stats panel has nothing more to print, and
 * neither is worth an error modal on a screen that otherwise works. So this
 * hook, like `useTeamStats`, does not call `showApiError`.
 *
 * Every label, value and rank arrives server rendered; this file only picks
 * the state.
 */
import { useTeamSeasonStatsGet } from "@sportsmet/sports-sdk/private";

export type typeTeamSeasonRankRow = {
  rowKey: string;
  label: string;
  displayValue: string;
  rankLabel: string | null;
};

export type typeTeamSeasonRankSection = {
  sectionKey: string;
  label: string;
  rows: typeTeamSeasonRankRow[];
};

export type typeTeamSeasonRanksState =
  | { kind: "absent" }
  | { kind: "loading" }
  | { kind: "ready"; sections: typeTeamSeasonRankSection[] };

type typeWire = {
  sections: {
    sectionKey: string;
    label: string;
    rows: {
      rowKey: string;
      label: string;
      displayValue: string;
      rankLabel?: string | null;
    }[];
  }[];
};

export function resolveTeamSeasonRanksState(input: {
  isPending: boolean;
  errorCode: string | null;
  data: typeWire | null;
}): typeTeamSeasonRanksState {
  if (input.data !== null) {
    const sections = input.data.sections
      .map((section) => ({
        sectionKey: section.sectionKey,
        label: section.label,
        rows: section.rows.map((row) => ({
          rowKey: row.rowKey,
          label: row.label,
          displayValue: row.displayValue,
          rankLabel: row.rankLabel ?? null,
        })),
      }))
      .filter((section) => section.rows.length > 0);
    return sections.length > 0
      ? { kind: "ready", sections }
      : { kind: "absent" };
  }
  if (input.errorCode === null && input.isPending) return { kind: "loading" };
  return { kind: "absent" };
}

export function useTeamSeasonRanks(teamId: string): typeTeamSeasonRanksState {
  const read = useTeamSeasonStatsGet(
    { teamId },
    { enabled: teamId.length > 0 },
  );
  if (teamId.length === 0) return { kind: "absent" };
  const envelope = read.data;
  return resolveTeamSeasonRanksState({
    isPending: read.isPending,
    errorCode: envelope?.error?.code ?? (read.isError ? "UNKNOWN" : null),
    data: envelope?.data ?? null,
  });
}
